import { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Button, ButtonGroup, Spinner } from 'react-bootstrap'
import { MdClose } from 'react-icons/md'

import './GoalCompletionModal.css'
import { getDefaultGoalAnswers } from './goalUtils'

function GoalCompletionModal({ completion, onClose, onSubmit, displayMode = 'fullscreen', statusText = 'Attempts and duration have already been recorded.' }) {
    const [answers, setAnswers] = useState(() => getDefaultGoalAnswers(completion?.goals || []))
    const [timerCancelled, setTimerCancelled] = useState(false)
    const [remainingSeconds, setRemainingSeconds] = useState(30)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState('')
    const closeTimerRef = useRef(null)
    const countdownRef = useRef(null)

    useEffect(() => {
        setAnswers(getDefaultGoalAnswers(completion?.goals || []))
        setTimerCancelled(false)
        setRemainingSeconds(30)
        setSubmitting(false)
        setError('')
    }, [completion])

    useEffect(() => {
        if (!completion || timerCancelled) return undefined
        closeTimerRef.current = window.setTimeout(onClose, 30000)
        countdownRef.current = window.setInterval(() => {
            setRemainingSeconds((seconds) => Math.max(0, seconds - 1))
        }, 1000)
        return () => {
            window.clearTimeout(closeTimerRef.current)
            window.clearInterval(countdownRef.current)
        }
    }, [completion, timerCancelled, onClose])

    const cancelTimer = () => {
        if (timerCancelled) return
        setTimerCancelled(true)
        window.clearTimeout(closeTimerRef.current)
        window.clearInterval(countdownRef.current)
    }

    const answerPayload = useMemo(() => (completion?.goals || []).map((goal) => ({
        goalId: String(goal._id),
        answers: (goal.questions || []).map((question) => ({
            questionId: question.id,
            optionId: answers[String(goal._id)]?.[question.id] || question.options?.[0]?.id || '',
        })),
    })), [answers, completion])

    const submit = async () => {
        cancelTimer()
        setSubmitting(true)
        setError('')
        try {
            await onSubmit(answerPayload)
        } catch (submitError) {
            setError(submitError.message || 'Could not save goal answers.')
            setSubmitting(false)
        }
    }

    if (!completion) return null

    return (
        <div className={`goal-completion-overlay goal-completion-overlay--${displayMode}`} onPointerDown={cancelTimer} onKeyDownCapture={cancelTimer}>
            <div className={`goal-completion-modal goal-completion-modal--${displayMode}`} role="dialog" aria-modal="true" aria-labelledby="goal-completion-title">
                <div className="goal-completion-header">
                    <div>
                        <h2 id="goal-completion-title">Goal check-in</h2>
                        <p>{timerCancelled ? 'Auto-close cancelled' : `Closes in ${remainingSeconds}s without interaction`}</p>
                    </div>
                    <Button variant="outline-light" className="goal-completion-close" onClick={onClose} aria-label="Close goal form"><MdClose /></Button>
                </div>

                {error && <Alert variant="danger">{error}</Alert>}

                <div className="goal-completion-sections">
                    {completion.goals.map((goal) => (
                        <section className="goal-completion-section" key={goal._id}>
                            <h3>{goal.name}</h3>
                            {(goal.questions || []).map((question) => (
                                <div className="goal-completion-question" key={question.id}>
                                    <div className="goal-completion-question-label">{question.label}</div>
                                    <ButtonGroup className="goal-completion-choices">
                                        {(question.options || []).map((option) => {
                                            const selected = answers[String(goal._id)]?.[question.id] === option.id
                                            return (
                                                <Button
                                                    key={option.id}
                                                    variant={selected ? 'primary' : 'outline-light'}
                                                    onClick={() => setAnswers((current) => ({
                                                        ...current,
                                                        [String(goal._id)]: {
                                                            ...current[String(goal._id)],
                                                            [question.id]: option.id,
                                                        },
                                                    }))}
                                                >
                                                    {option.label}
                                                </Button>
                                            )
                                        })}
                                    </ButtonGroup>
                                </div>
                            ))}
                        </section>
                    ))}
                </div>

                <div className="goal-completion-footer">
                    <span>{statusText}</span>
                    <div>
                        <Button variant="outline-light" onClick={onClose}>Close</Button>
                        <Button variant="success" disabled={submitting} onClick={submit}>
                            {submitting && <Spinner animation="border" size="sm" />} Submit
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default GoalCompletionModal
